import { apiRequest } from "../util/apiUtils";

/**
 * Shopify gift calls.
 *
 * A gift is paid on Shopify before any book exists. The recipient gets an
 * emailed link (/gift?t=...) that signs them in; from then on these calls
 * drive the gift page: their books, how the paid copies are split, and the
 * address. Every call is checked against the session on the server.
 *
 * Calls answer `{ success, data | message }`; a network failure answers the
 * same shape so screens never have to tell the two apart.
 */

const NETWORK = { success: false, networkError: true, message: "We couldn't reach the server. Please check your connection and try again." };

// apiRequest resolves to the response for any HTTP status (its body carries the
// API's plain message) and to undefined when the request never got an answer.
async function call(method, path, options) {
  const res = await apiRequest(method, path, options);
  const data = res?.data;
  return data && typeof data === "object" ? data : NETWORK;
}

/** Sign in from the emailed link. Answers `{ ok, accessToken, gift }` or `{ ok: false, message }`. */
export const claimGift = async (token) => {
  const res = await apiRequest("post", "gift/claim", { body: { token } });
  const data = res?.data;
  return data && typeof data === "object" ? data : { ok: false, networkError: true, message: NETWORK.message };
};

// A POST with no payload still needs a JSON body: apiRequest otherwise sends
// "null", which the API's strict JSON parser rejects.
const EMPTY_BODY = { body: {} };

export const getMyGifts = () => call("get", "gift/mine");
export const getGift = (giftId) => call("get", `gift/${giftId}`);
export const setGiftBookPlan = (giftId, plan) => call("put", `gift/${giftId}/plan`, { body: { plan } });
export const saveGiftAllocations = (giftId, allocations) =>
  call("put", `gift/${giftId}/allocations`, { body: { allocations } });
export const saveGiftAddress = (giftId, address) => call("put", `gift/${giftId}/address`, { body: { address } });
export const markGiftBookDone = (giftId, bookId) => call("post", `gift/${giftId}/books/${bookId}/done`, EMPTY_BODY);
export const reopenGiftBook = (giftId, bookId) => call("post", `gift/${giftId}/books/${bookId}/reopen`, EMPTY_BODY);
export const removeGiftBook = (giftId, bookId) => call("post", `gift/${giftId}/books/${bookId}/remove`, EMPTY_BODY);
export const submitGift = (giftId) => call("post", `gift/${giftId}/submit`, EMPTY_BODY);

/** The gift a book belongs to, or null for an ordinary book. */
export const giftIdOfBook = (book) => {
  const id = book && book.gift && book.gift.giftOrderId;
  return id ? String(id) : null;
};

export const giftPagePath = (giftId) => `/gift/books?id=${encodeURIComponent(giftId)}`;
/** Gift 1: who it's from, the message and the address (docs/design/HANDOFF.md). */
export const giftWelcomePath = (giftId) => `/gift/welcome?id=${encodeURIComponent(giftId)}`;
/** Gift 2: how to split the copies into books. */
export const giftPlanPath = (giftId) => `/gift/plan?id=${encodeURIComponent(giftId)}`;

/** Start a new book that belongs to this gift (BookCreation reads `gift`). */
export const newGiftBookPath = (giftId) =>
  `/create-book?stepCode=Create_New_Book&gift=${encodeURIComponent(giftId)}`;

/** The gift id a page was opened for (`?gift=` on /create-book, `?id=` on gift pages). */
export const giftIdFromSearch = (search, key = "gift") => {
  const value = new URLSearchParams(search || "").get(key) || "";
  return /^[a-f0-9]{24}$/i.test(value) ? value : null;
};
